import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { DemoProvider, useDemo } from './context/DemoContext';
import Sidebar from './components/shell/Sidebar';
import Topbar from './components/shell/Topbar';
import Walkthrough from './components/Walkthrough';
import { canSee, homeFor } from './components/shell/nav';
import { Loading, Restricted } from './components/common';
import Overview from './pages/Overview';
import Applications from './pages/Applications';
import ApplicationNew from './pages/ApplicationNew';
import ApplicationDetail from './pages/ApplicationDetail';
import Smes from './pages/Smes';
import SmeDetail from './pages/SmeDetail';
import Equipment from './pages/Equipment';
import EquipmentDetail from './pages/EquipmentDetail';
import Finance from './pages/Finance';
import Interventions from './pages/Interventions';
import CaseDetail from './pages/CaseDetail';
import Insights from './pages/Insights';
import Workspace from './pages/Workspace';
import Admin from './pages/Admin';
import Sources from './pages/Sources';
import Stakeholders from './pages/Stakeholders';

function Guarded({ children }) {
  const { me } = useDemo();
  const loc = useLocation();
  if (!me) return <Loading label="Loading demonstration persona…" />;
  if (!canSee(me.role, loc.pathname)) return <Restricted />;
  return children;
}

function Home() {
  const { me } = useDemo();
  return me ? <Navigate to={homeFor(me.role)} replace /> : <Loading />;
}

function Shell() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    document.title = 'barlowiq.com';
  }, [loc.pathname]);
  const routes = [
    ['/overview', Overview], ['/applications', Applications], ['/applications/new', ApplicationNew], ['/applications/:id', ApplicationDetail],
    ['/smes', Smes], ['/smes/:id', SmeDetail], ['/equipment', Equipment], ['/equipment/:id', EquipmentDetail], ['/finance', Finance],
    ['/interventions', Interventions], ['/interventions/:id', CaseDetail], ['/insights', Insights], ['/workspace', Workspace], ['/stakeholders', Stakeholders],
    ['/admin', Admin], ['/sources', Sources],
  ];
  return (
    <div className="min-h-screen bg-[#0A0F1D] grain">
      <Sidebar open={open} onClose={() => setOpen(false)} />
      <div className="lg:pl-64">
        <Topbar onMenu={() => setOpen(true)} />
        <main className="mx-auto max-w-[1680px] space-y-5 p-4 sm:p-6">
          <Routes>
            <Route path="/" element={<Home />} />
            {routes.map(([p, C]) => <Route key={p} path={p} element={<Guarded><C /></Guarded>} />)}
            <Route path="*" element={<Home />} />
          </Routes>
        </main>
        <footer className="border-t border-[#1E2A45] px-6 py-4 text-[11px] text-slate-500" data-testid="app-footer">
          Barloworld Equipment | Equipment Finance &amp; SME Performance Intelligence — Nsync Demonstration Concept. Proposed demonstration only; not an
          officially commissioned, approved or operational Barloworld system. All SMEs, assets, finance and performance records are synthetic.
        </footer>
      </div>
      <Walkthrough />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <DemoProvider>
        <Shell />
        <Toaster theme="dark" position="top-center" richColors />
      </DemoProvider>
    </BrowserRouter>
  );
}
